import { TextFieldModule } from '@angular/cdk/text-field';
import { NgClass, DatePipe } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation, input, output, signal, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { AiChatMessage, AiChatUser, AiChatFavoriteTemplate, MessageRole, SpinnerType } from './ai-chat.types';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MarkdownService } from '@aurora';

/**
 * To use component:
 *
 * <au-ai-chat
  [messages]="chatMessages"
  [currentUser]="user"
  [favoriteTemplates]="favoritePrompts"
  [showSpinner]="isLoading"
  [animateLastMessage]="true"
  [showEditFunctionality]="true"
  [showFavoriteFunctionality]="true"
  [showDeleteFavorites]="false"
  (sendMessage)="onSendMessage($event)"
  (editMessage)="onEditMessage($event)"
  (favoriteMessage)="onToggleFavorite($event)"
  (deleteFavorite)="onDeleteFavorite($event)"
  (scroll)="onChatScroll($event)">
</au-ai-chat>
 */

@Component({
    selector: 'au-ai-chat',
    templateUrl: './ai-chat.component.html',
    styles: `
        .assistant-message {
            background-color: #f0f0f0;
            border-radius: 8px 8px 8px 0;
        }

        .user-message {
            background-color: #e1f5fe;
            border-radius: 8px 8px 0 8px;
        }

        .message-actions {
            opacity: 0;
            transition: opacity 0.2s;
        }

        .message-container:hover .message-actions {
            opacity: 1;
        }

        .favorite-templates {
            max-height: 300px;
            overflow-y: auto;
        }

        .mat-tooltip {
            font-size: 14px;
            white-space: pre-wrap;
            max-width: 300px;
            word-wrap: break-word;
            padding: 8px 12px;
        }

        .favorite-item {
            position: relative;
            transition: background-color 0.2s;
        }

        .favorite-item:hover {
            background-color: rgba(0, 0, 0, 0.04);
        }

        /* Estilos para el botón de eliminar */
        .delete-favorite-btn {
            width: 24px;
            height: 24px;
            line-height: 24px;
            opacity: 0.7;
            transition: opacity 0.2s;
        }

        .delete-favorite-btn:hover {
            opacity: 1;
        }

        .delete-favorite-btn .mat-icon {
            font-size: 18px;
            width: 18px;
            height: 18px;
            line-height: 18px;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        DatePipe,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatTooltipModule,
        NgClass,
        TextFieldModule
    ],
})
export class AiChatComponent {
    @ViewChild('messageInput') messageInput: { nativeElement: HTMLTextAreaElement } | undefined;
    @ViewChild('messagesContainer') messagesContainer: { nativeElement: HTMLDivElement } | undefined;

    // Enums
    messageRole = MessageRole;
    spinnerType = SpinnerType;

    // Inputs
    messages = input.required<AiChatMessage[]>({ alias: 'messages' });
    currentUser = input.required<AiChatUser>({ alias: 'currentUser' });
    favoriteTemplates = input<AiChatFavoriteTemplate[]>([], { alias: 'favoriteTemplates' });
    showSpinner = input<boolean>(false, { alias: 'showSpinner' });
    typeSpinner = input<SpinnerType>(SpinnerType.BUBBLE, { alias: 'typeSpinner' });
    animateLastMessage = input<boolean>(false, { alias: 'animateLastMessage' });
    typingSpeed = input<number>(20, { alias: 'typingSpeed' });
    showDeleteFavorites = input<boolean>(false, { alias: 'showDeleteFavorites' });
    showEditFunctionality = input<boolean>(false, { alias: 'showEditFunctionality' });
    showFavoriteFunctionality = input<boolean>(false, { alias: 'showFavoriteFunctionality' })

    // Estados internos
    isTyping = signal<boolean>(false);
    typedMessage = signal<string>('');
    editingMessage = signal<AiChatMessage | null>(null);
    messageInputValue = signal<string>('');
    deleteFavorite = output<string>();
    private lastMessageId: string | null = null;

    // Outputs
    sendMessage = output<string>();
    editMessage = output<{messageId: string, newContent: string}>();
    favoriteMessage = output<AiChatMessage>();
    addToFavorites = output<string>();
    removeFromFavorites = output<string>();
    scroll = output<Event>();
    navigateToNote = output<{noteId: string, noteName: string}>();

    constructor(
        private markdownService: MarkdownService,
    ) {
        // Variable para evitar procesar múltiples veces el mismo mensaje
        let lastProcessedMsgId: string | null = null;

        /**
         * Efecto que detecta cuando se añade un nuevo mensaje del asistente
         * e inicia la animación de escritura si está habilitada.
         * Usa lastProcessedMsgId para evitar mostrar la animación más de una vez
         * para el mismo mensaje, evitando regeneraciones innecesarias.
         */
        effect(() =>
        {
            const msgs = this.messages();
            if (!msgs || msgs.length === 0) return;

            const lastMsg = msgs[msgs.length - 1];
            if (!lastMsg || lastMsg.id === lastProcessedMsgId) return;

            untracked(() =>
            {
                if (lastMsg.role === MessageRole.ASSISTANT && this.animateLastMessage())
                {
                    lastProcessedMsgId = lastMsg.id;

                    this.isTyping.set(true);
                    this.typedMessage.set('');
                    this.typewriterEffect(lastMsg.message);
                }
            });
        });
        effect(() => {
            const msgs = this.messages();
            if (msgs && msgs.length > 0) {
                const lastMsg = msgs[msgs.length - 1];

                // Solo hacer scroll si:
                // 1. Hay un nuevo último mensaje (ID diferente)
                // 2. El último mensaje es del asistente (respuesta IA)
                if (lastMsg && lastMsg.id !== this.lastMessageId) {
                    if (lastMsg.role === MessageRole.ASSISTANT) {
                        this.scrollToBottom();
                    }
                    this.lastMessageId = lastMsg.id;
                }
            }
        });
    }

    /**
     * Crea un efecto de escritura letra por letra para simular que el asistente está escribiendo en tiempo real.
     * Añade cada carácter del mensaje gradualmente según la velocidad configurada y aplica formato Markdown.
     * @param message El mensaje completo que se mostrará letra por letra
     */
    typewriterEffect(message: string): void
    {
        if (this.typedMessage().length < message.length)
        {
            // Obtener el texto parcial
            const partialText = message.substring(0, this.typedMessage().length + 1);

            // Convertir a HTML con Markdown
            this.typedMessage.set(
                this.markdownService.markdownToHtml(partialText) as string
            );

            setTimeout(() => this.typewriterEffect(message), this.typingSpeed());
        } else {
            this.isTyping.set(false);
        }
    }

    /**
     * Maneja el envío de mensajes desde el campo de entrada.
     * Si hay un mensaje en edición, emite un evento para actualizar ese mensaje.
     * De lo contrario, emite un evento para enviar un nuevo mensaje.
     */
    handleSendMessage(): void
    {
        const message = this.messageInputValue();
        if (!message || !message.trim()) return;

        const editingMsg = this.editingMessage();
        if (editingMsg)
        {
            this.editMessage.emit({
                messageId: editingMsg.id,
                newContent: message
            });
            this.editingMessage.set(null);
        } else {
            this.sendMessage.emit(message);
        }

        this.messageInputValue.set('');

        this.scrollToBottom();
    }

    /**
     * Inicia el modo de edición para un mensaje existente.
     * Coloca el contenido del mensaje en el campo de entrada y establece el foco.
     * @param message El mensaje que se va a editar
     */
    startEditMessage(message: AiChatMessage): void
    {
        this.editingMessage.set(message);
        this.messageInputValue.set(message.message);

        setTimeout(() => {
            if (this.messageInput?.nativeElement) {
                this.messageInput.nativeElement.focus();
            }
        }, 100);
    }

    /**
     * Cancela el modo de edición actual y limpia el campo de entrada.
     */
    cancelEdit(): void
    {
        this.editingMessage.set(null);
        this.messageInputValue.set('');
    }

    /**
     * Emite un evento para marcar/desmarcar un mensaje como favorito.
     * @param message El mensaje que se va a marcar o desmarcar como favorito
     */
    toggleFavorite(message: AiChatMessage): void
    {
        this.favoriteMessage.emit(message);
    }

    /**
     * Inserta el contenido de una plantilla favorita en el campo de entrada.
     * Establece el foco en el campo de entrada después de insertar el texto.
     * @param template La plantilla favorita cuyo contenido se va a utilizar
     */
    useTemplate(template: AiChatFavoriteTemplate): void
    {
        this.messageInputValue.set(template.content);

        setTimeout(() =>
        {
            if (this.messageInput?.nativeElement) {
                this.messageInput.nativeElement.focus();
            }
        }, 100);
    }

    /**
     * Emite un evento cuando ocurre un desplazamiento en el área de mensajes.
     * Útil para implementar carga de mensajes anteriores o seguimiento de lectura.
     * @param event El evento de desplazamiento
     */
    handleScroll(event: Event): void
    {
        this.scroll.emit(event);
    }

    /**
     * Maneja la pulsación de la tecla Enter en el campo de entrada.
     * Envía el mensaje si no se presiona Shift+Enter (que añade una nueva línea).
     * @param event El evento de teclado
     */
    onEnterKey(event: KeyboardEvent): void
    {
        if (!event.shiftKey)
        {
            this.handleSendMessage();
            event.preventDefault();
        }
    }

    /**
     * Emite un evento para eliminar un favorito de la lista
     * @param templateId El ID del template favorito que se va a eliminar
     * @param event El evento del click para evitar propagación
     */
    removeFavorite(templateId: string, event: MouseEvent): void
    {
        event.stopPropagation(); // Evitar que se seleccione el favorito al hacer clic en eliminar
        this.deleteFavorite.emit(templateId);
    }

    /**
     * Hace scroll hasta el final del contenedor de mensajes
     */
    scrollToBottom(): void {
        setTimeout(() => {
            if (this.messagesContainer?.nativeElement) {
                const container = this.messagesContainer.nativeElement;
                container.scrollTop = container.scrollHeight;
            }
        }, 100);
    }

    /**
     * Convierte texto Markdown a HTML usando el servicio de Markdown
     * @param text El texto en formato Markdown
     * @returns El HTML resultante
     */
    renderMarkdown(text: string): string {
        if (!text) return '';
        return this.markdownService.markdownToHtml(text) as string;
    }

    /**
     * Verifica si un mensaje tiene notas válidas para mostrar
     * @param message El mensaje a verificar
     * @returns true si tiene notas válidas, false en caso contrario
     */
    hasValidNotes(message: any): boolean {
        return message && 
               message.meta && 
               message.meta.notes && 
               Array.isArray(message.meta.notes) && 
               message.meta.notes.length > 0;
    }

    /**
     * Emite un evento para navegar a una nota específica
     * @param noteId El ID de la nota
     * @param noteName El nombre de la nota
     */
    onNavigateToNote(noteId: string, noteName: string): void {
        this.navigateToNote.emit({ noteId, noteName });
    }
}
